import { useCallback, useMemo, useRef, useState } from "react";

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

export function usePanZoom({
  initialX = 0,
  initialY = 0,
  initialScale = 1,
  minScale = 0.4,
  maxScale = 2.5,
} = {}) {
  const [transform, setTransform] = useState({
    x: initialX,
    y: initialY,
    scale: initialScale,
  });

  const dragRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });

  const zoomAt = useCallback(
    (nextScale, clientX, clientY, containerRect) => {
      setTransform((t) => {
        const scale = clamp(nextScale, minScale, maxScale);
        const k = scale / t.scale;
        const px = clientX - containerRect.left;
        const py = clientY - containerRect.top;
        const x = px - (px - t.x) * k;
        const y = py - (py - t.y) * k;
        return { x, y, scale };
      });
    },
    [minScale, maxScale],
  );

  const onWheel = useCallback(
    (e, containerRect) => {
      e.preventDefault();
      const delta = e.deltaY;
      const factor = delta < 0 ? 1.08 : 1 / 1.08;
      zoomAt(transform.scale * factor, e.clientX, e.clientY, containerRect);
    },
    [transform.scale, zoomAt],
  );

  const onPointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    dragRef.current.active = true;
    dragRef.current.pointerId = e.pointerId;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.originX = transform.x;
    dragRef.current.originY = transform.y;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [transform.x, transform.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    if (dragRef.current.pointerId !== e.pointerId) return;
    e.preventDefault();
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setTransform((t) => ({ ...t, x: dragRef.current.originX + dx, y: dragRef.current.originY + dy }));
  }, []);

  const onPointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    if (dragRef.current.pointerId !== e.pointerId) return;
    dragRef.current.active = false;
    dragRef.current.pointerId = null;
  }, []);

  const reset = useCallback(() => {
    setTransform({ x: initialX, y: initialY, scale: initialScale });
  }, [initialX, initialY, initialScale]);

  const zoomIn = useCallback((containerRect) => {
    zoomAt(transform.scale * 1.12, containerRect.left + containerRect.width / 2, containerRect.top + containerRect.height / 2, containerRect);
  }, [transform.scale, zoomAt]);

  const zoomOut = useCallback((containerRect) => {
    zoomAt(transform.scale / 1.12, containerRect.left + containerRect.width / 2, containerRect.top + containerRect.height / 2, containerRect);
  }, [transform.scale, zoomAt]);

  const svgTransform = useMemo(
    () => `translate(${transform.x} ${transform.y}) scale(${transform.scale})`,
    [transform.x, transform.y, transform.scale],
  );

  const setPan = useCallback((x, y) => {
    setTransform((t) => ({ ...t, x, y }));
  }, []);

  return {
    transform,
    setPan,
    svgTransform,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
    onWheel,
    zoomIn,
    zoomOut,
    reset,
  };
}
